import type { RouteLocationRaw } from "vue-router";
import { adminModules } from "./admin";
import { businessModules } from "./business";
import { coreModules } from "./core";
import { crmModules } from "./crm";
import { graveyardModules } from "./graveyard";
import { healthcareModules } from "./healthcare";
import { petTransportModules } from "./petTransport";
import { reportEngineModules } from "./reports";
import type {
  AppModuleDefinition,
  AppNavChildDefinition,
  AppPageDefinition,
  AppSearchDefinition,
  AppShortcutDefinition,
} from "./types";

export * from "./keys";
export type * from "./types";

export interface AppNavItem {
  title: string;
  to?: RouteLocationRaw;
  icon?: { icon: string };
  accessKey?: string;
  permission?: string | string[];
  children?: AppNavItem[];
  /**
   * Phone bottom-tab priority, carried through from `module.nav.mobileOrder`. Consumed only by
   * `src/config/mobileNav.ts`; the sidebar renderers ignore it.
   */
  mobileOrder?: number;
  /** Registry module key, so the phone shell can exclude specific modules (e.g. the /modules stub). */
  moduleKey?: string;
}

export const appModules: AppModuleDefinition[] = [
  ...coreModules,
  ...healthcareModules,
  ...graveyardModules,
  ...crmModules,
  ...reportEngineModules,
  ...businessModules,
  ...petTransportModules,
  ...adminModules,
];

export const appPages: AppPageDefinition[] = appModules.flatMap(module => module.pages);
export const appShortcuts: AppShortcutDefinition[] = appModules
  .flatMap(module => module.shortcuts ?? [])
  .sort((a, b) => a.order - b.order);

export const appSearchEntries: AppSearchDefinition[] = appModules
  .flatMap(module => module.search ?? [])
  .sort((a, b) => a.order - b.order);

function compareOrder(
  a: { nav?: { order?: number } },
  b: { nav?: { order?: number } },
) {
  return (a.nav?.order ?? 0) - (b.nav?.order ?? 0);
}

function firstPageTarget(page: AppPageDefinition): RouteLocationRaw | undefined {
  if (page.nav?.to)
    return page.nav.to;

  if (page.routeNames?.[0])
    return { name: page.routeNames[0] };

  const path = page.path ?? page.paths?.[0];

  return path ? { path } : undefined;
}

function childToNavItem(
  child: AppNavChildDefinition,
  page: AppPageDefinition,
): AppNavItem {
  return {
    title: child.titleKey,
    to: child.to,
    // Falls back to the parent page's icon so a child in the phone icon grid is never a bare circle.
    icon: child.icon
      ? { icon: child.icon }
      : (page.nav?.icon ? { icon: page.nav.icon } : undefined),
    accessKey: page.key,
    permission: page.fallbackRoles,
  };
}

function pageToNavItems(page: AppPageDefinition): AppNavItem[] {
  const nav = page.nav;

  if (!nav)
    return [];

  if (nav.children?.length && nav.flattenChildren) {
    return [...nav.children]
      .sort((a, b) => a.order - b.order)
      .map(child => childToNavItem(child, page));
  }

  if (nav.children?.length) {
    return [{
      title: nav.titleKey,
      icon: nav.icon ? { icon: nav.icon } : undefined,
      accessKey: page.key,
      permission: page.fallbackRoles,
      children: [...nav.children]
        .sort((a, b) => a.order - b.order)
        .map(child => childToNavItem(child, page)),
    }];
  }

  return [{
    title: nav.titleKey,
    to: firstPageTarget(page),
    icon: nav.icon ? { icon: nav.icon } : undefined,
    accessKey: page.key,
    permission: page.fallbackRoles,
  }];
}

function pagesForModule(module: AppModuleDefinition) {
  return appPages
    .filter(page =>
      page.nav
      && (
        page.nav.parentModuleKey === module.key
        || (!page.nav.parentModuleKey && module.pages.some(modulePage => modulePage.key === page.key))
      ),
    )
    .sort(compareOrder);
}

export function buildAppNavItems(): AppNavItem[] {
  return appModules
    .filter(module => module.nav)
    .sort(compareOrder)
    .flatMap((module): AppNavItem[] => {
      const pages = pagesForModule(module);
      const children = pages.flatMap(pageToNavItems);
      const firstPage = pages[0];

      if (module.nav?.singlePage && firstPage) {
        const item: AppNavItem = {
          title: module.nav.titleKey ?? firstPage.nav?.titleKey ?? module.labelKey,
          icon: { icon: module.nav.icon ?? module.icon },
          to: firstPageTarget(firstPage),
          accessKey: firstPage.key,
          permission: firstPage.fallbackRoles,
          mobileOrder: module.nav.mobileOrder,
          moduleKey: module.key,
        };

        return [item];
      }

      const item: AppNavItem = {
        title: module.nav?.titleKey ?? module.labelKey,
        icon: { icon: module.nav?.icon ?? module.icon },
        accessKey: module.key,
        permission: module.fallbackRoles,
        mobileOrder: module.nav?.mobileOrder,
        moduleKey: module.key,
        children,
      };

      return [item];
    });
}

export function getNavbarSuggestionGroups() {
  const groups = new Map<string, AppSearchDefinition[]>();

  for (const entry of appSearchEntries) {
    const existing = groups.get(entry.group) ?? [];

    existing.push(entry);
    groups.set(entry.group, existing);
  }

  return [...groups.entries()].map(([title, content]) => ({
    title,
    content: content
      .sort((a, b) => a.order - b.order)
      .map(entry => ({
        icon: entry.icon,
        title: entry.title,
        url: entry.url,
        accessKey: entry.accessKey,
      })),
  }));
}

export function getNavbarNoDataSuggestions() {
  return appSearchEntries
    .filter(entry => entry.noData)
    .sort((a, b) => a.order - b.order)
    .map(entry => ({
      icon: entry.icon,
      title: entry.title,
      url: entry.url,
      accessKey: entry.accessKey,
    }));
}
